// RouteTemplate.jsx
// -------------------------------------------
// Generic template for point-to-point route pages
// Example: /heathrow-to-slough
//
// ✅ HOW TO USE FOR A NEW ROUTE
// 1. Duplicate this file and rename it, e.g. HeathrowToSlough.jsx
// 2. Update all places marked with "// 🔧 EDIT" to match the new route
//    - SEO <Helmet> block (title, description, canonical, JSON-LD)
//    - Headline text (H1 + intro)
//    - Bullet points / journey details (distance, time, fare range)
//    - Preset pickup/dropoff in <PostcodeQuote>
//    - FAQ questions and answers
//    - Internal links where needed
// 3. Import this component in App.js and add a <Route> for the new path
//
// This version is tailored for: /heathrow-to-slough

import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PostcodeQuotenew from "./PostcodeQuotenew";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "./Home.css";
import "../styles/routePages.css"; // ✅ shared styles for all route pages

const RouteTemplate = () => {
  const scrollToForm = () => {
    const el = document.getElementById("route-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="route-page">
      {/* ================= SEO: Helmet Block ================= */}
      {/* 🔧 EDIT FOR NEW ROUTE: update title, description, canonical, keywords, JSON-LD */}
      <Helmet>
        <title>
          Heathrow to Slough Taxi – Fixed Airport Transfers SL1–SL3 | CheapCabGrab
        </title>
        <meta
          name="description"
          content="Book a fixed-price taxi from Heathrow Airport to Slough (SL1–SL3). Instant online quote, licensed drivers, flight tracking, 24/7 support and no surge pricing with CheapCabGrab."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-to-slough" />
        <meta
          name="keywords"
          content="Heathrow to Slough taxi, taxi Heathrow Airport to Slough, SL1 taxi from Heathrow, Heathrow to Slough minicab, airport transfer Heathrow to Slough, CheapCabGrab"
        />

        {/* Open Graph for social */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Heathrow to Slough Taxi – Fixed Airport Transfers SL1–SL3"
        />
        <meta
          property="og:description"
          content="Reliable Heathrow to Slough taxis with fixed fares, licensed drivers and live flight tracking. Book online in seconds with CheapCabGrab."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/heathrow-to-slough"
        />

        {/* JSON-LD – TaxiService + Route for this specific journey */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            name: "CheapCabGrab – Heathrow to Slough Taxi",
            url: "https://www.cheapcabgrab.com/heathrow-to-slough",
            areaServed: ["Heathrow Airport", "Slough SL1", "Slough SL2", "Slough SL3"],
            serviceType: "Airport transfer",
            provider: {
              "@type": "Organization",
              name: "CheapCabGrab",
            },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Heathrow to Slough Taxi Routes",
              itemListElement: [
                {
                  "@type": "Offer",
                  name: "Heathrow Airport to Slough fixed-price taxi",
                  description:
                    "Fixed-price taxi transfers from Heathrow Airport terminals to Slough (SL1–SL3) with licensed drivers and flight tracking.",
                },
              ],
            },
            potentialAction: {
              "@type": "ReserveAction",
              target: "https://www.cheapcabgrab.com/heathrow-to-slough",
              result: {
                "@type": "Reservation",
                name: "Taxi booking from Heathrow to Slough",
              },
            },
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <main className="route-main">
        {/* 🟦 HERO */}
        <section className="home-hero">
          <div className="home-hero-shell">
            <div className="home-hero-copy">
              <h1>Heathrow to Slough Taxi – Fixed-Price Transfers</h1>
              <p>SL1–SL3 specialists with flight tracking</p>
              <p>
                Book reliable taxis from Heathrow Terminals 2–5 to Slough. Fixed upfront fares, licensed drivers,
                meet &amp; greet and secure online booking — no surge pricing.
              </p>
            </div>

            <div className="home-hero-form-row" id="route-quote-form">
              <PostcodeQuotenew presetPickup="Heathrow Airport" presetDropoff="Slough, SL1" />
            </div>

            <ul className="home-hero-flags">
              <li>✅ Fixed fares — no meters</li>
              <li>✅ Flight tracking + meet &amp; greet available</li>
              <li>✅ Licensed drivers, luggage-ready vehicles</li>
              <li>✔ Saloon, MPV, 8–16 seat minibus &amp; coach options</li>
            </ul>

            <div className="home-hero-reviews">
              <ReviewsBar pageKey="/heathrow-to-slough" count={3} />
            </div>
          </div>
        </section>

        {/* FLOATING CTA */}
        <button className="home-floating-cta" onClick={scrollToForm}>
          🚕 Book Heathrow → Slough
        </button>

        {/* ================= ROUTE OVERVIEW ================= */}
        {/* 🔧 EDIT distance / duration / price range per route */}
        <section className="route-section" id="route-overview">
          <header className="route-section-header">
            <h2>Heathrow ↔ Slough at a glance</h2>
            <p>
              A short, busy airport corridor served 24/7 by CheapCabGrab. Ideal for
              residents, hotels, business parks and travellers connecting via Heathrow.
            </p>
          </header>

          <div className="route-overview-grid">
            <div className="route-overview-card">
              <h3>Journey basics</h3>
              <ul>
                <li>
                  📏 Distance: <strong>Approx. 8–12 miles</strong> (depending on pickup
                  point)
                </li>
                <li>
                  ⏱ Typical time: <strong>15–30 minutes</strong>
                </li>
                <li>
                  💷 Typical saloon fare: <strong>from £35–£55</strong>
                </li>
                <li>🏙 Areas covered: SL1, SL2, SL3, Slough Trading Estate & more</li>
              </ul>
            </div>

            <div className="route-overview-card">
              <h3>Great for</h3>
              <ul>
                <li>✈️ Residents flying from Heathrow</li>
                <li>🏨 Hotel guests in Slough, Langley & Burnham</li>
                <li>💼 Staff working on Slough Trading Estate</li>
                <li>👨‍👩‍👧 Families with luggage & child seats</li>
              </ul>
            </div>

            <div className="route-overview-card">
              <h3>Why CheapCabGrab?</h3>
              <ul>
                <li>🚕 Licensed local drivers with airport experience</li>
                <li>🔍 Transparent fares, no surge pricing</li>
                <li>📱 Instant online booking & confirmation</li>
                <li>🧾 Receipts for business & expense claims</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ================= INTERNAL LINKS & TOOLS ================= */}
        {/* 🔧 Adjust links depending on which town/airport the route touches */}
        <section className="route-section" id="route-tools">
          <header className="route-section-header">
            <h2>Useful tools for your Heathrow ⇆ Slough journey</h2>
            <p>Plan every part of your airport trip with CheapCabGrab tools.</p>
          </header>

          <div className="route-links-grid">
            <article className="route-link-card">
              <h3>✈ Heathrow Terminal Finder</h3>
              <p>
                Not sure which terminal your airline uses? Use our live terminal finder to
                check gates and departure points.
              </p>
              <Link to="/terminalFinder" className="route-link-btn">
                Open Terminal Finder
              </Link>
            </article>

            <article className="route-link-card">
              <h3>📞 Airline contact search</h3>
              <p>
                Quickly find customer service numbers, websites and support links for your
                airline.
              </p>
              <Link to="/airlineContacts" className="route-link-btn">
                Find airline contacts
              </Link>
            </article>

            <article className="route-link-card">
              <h3>🚐 Minibus & coach options</h3>
              <p>
                Travelling as a group from Heathrow to Slough? Upgrade to a minibus or coach
                for extra space and comfort.
              </p>
              <div className="route-link-btn-row">
                <Link to="/minibus" className="route-link-btn">
                  Heathrow minibus
                </Link>
                <Link to="/coach" className="route-link-btn route-link-btn-secondary">
                  Heathrow coach
                </Link>
              </div>
            </article>
          </div>
        </section>

        {/* ================= LOCAL AREAS / HOTELS ================= */}
        {/* 🔧 Tweak locations for each new route (e.g. Windsor, Maidenhead etc.) */}
        <section className="route-section" id="route-areas">
          <header className="route-section-header">
            <h2>Heathrow → Slough – popular pickup & drop-off points</h2>
            <p>
              We regularly serve homes, hotels and business locations across Slough and the
              Heathrow corridor.
            </p>
          </header>

          <div className="route-areas-grid">
            <article className="route-area-card">
              <h3>In and around Slough (SL1–SL3)</h3>
              <ul>
                <li>Slough town centre & bus station</li>
                <li>Slough train station</li>
                <li>Slough Trading Estate & Bath Road offices</li>
                <li>Wexham Park Hospital</li>
                <li>Langley, Colnbrook & Poyle</li>
              </ul>
              <Link to="/slough-taxi" className="route-link-inline">
                Slough taxis overview →
              </Link>
            </article>

            <article className="route-area-card">
              <h3>Heathrow & nearby hotels</h3>
              <ul>
                <li>All Heathrow terminals (T2, T3, T4, T5)</li>
                <li>Airport hotels on Bath Road</li>
                <li>Hotels near M4 junctions & business parks</li>
              </ul>
              <Link to="/heathrow-hotels" className="route-link-inline">
                See Heathrow hotel guide →
              </Link>
            </article>
          </div>
        </section>

        {/* ================= FAQs ================= */}
        {/* 🔧 Replace Q&A content to be specific for each route */}
        <section className="route-section" id="route-faq">
          <header className="route-section-header">
            <h2>Heathrow to Slough taxi – FAQs</h2>
            <p>
              A few common questions passengers ask when travelling between Heathrow Airport
              and Slough.
            </p>
          </header>

          <div className="route-faq-grid">
            <article className="route-faq-card">
              <h3>Do you track my incoming flight?</h3>
              <p>
                Yes. For Heathrow pickups we monitor your flight and adjust the dispatch time
                where possible. If your flight is severely delayed or diverted, just contact
                support and we will re-plan your pickup.
              </p>
            </article>

            <article className="route-faq-card">
              <h3>Can you collect from Slough train station or Trading Estate?</h3>
              <p>
                Absolutely. Slough station, the bus station, Slough Trading Estate and Bath
                Road offices are all regular pickup and drop-off points for CheapCabGrab.
              </p>
            </article>

            <article className="route-faq-card">
              <h3>Is meet & greet at Heathrow included?</h3>
              <p>
                Standard fares include curbside pickup. Meet & greet inside the terminal is
                available on request for an additional charge – ideal for visitors or
                vulnerable passengers.
              </p>
            </article>

            <article className="route-faq-card">
              <h3>Can I book a minibus from Heathrow to Slough?</h3>
              <p>
                Yes. For larger groups, luggage or work teams, we offer 7–14 seater minibuses
                and 25+ seater coaches through our PSV partners. Use the quote form or visit
                our minibus and coach pages for more details.
              </p>
            </article>

            <div className="route-faq-footer">
              <p>
                Need more details? Visit our full <Link to="/faq">FAQ page</Link> or contact
                support.
              </p>
            </div>
          </div>
        </section>

        {/* ================= SUPPORT / CONTACT ================= */}
        <section className="route-section" id="route-support">
          <header className="route-section-header">
            <h2>Need help with a Heathrow ⇆ Slough taxi booking?</h2>
            <p>
              If you have special requirements, multi-stop journeys or corporate bookings,
              our team is happy to help.
            </p>
          </header>

          <div className="route-support-box">
            <p>
              ✉ Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a>
            </p>
            <p>
              Or send us a message via our <Link to="/contact">contact form</Link> and we’ll
              get back to you as soon as possible.
            </p>
            <p>
              For other popular routes, see our pages for
              {" "}
              <Link to="/slough-taxi">Slough taxis</Link>,{" "}
              <Link to="/heathrow-taxi">Heathrow taxis</Link> and our dedicated
              {" "}
              <Link to="/fleet">fleet</Link> and{" "}
              <Link to="/our-drivers">driver standards</Link> pages.
            </p>
          </div>
        </section>

        {/* ================= CTA BUTTONS ================= */}
        {/* Desktop floating CTA */}
        <button
          className="route-floating-book-btn"
          onClick={() => {
            const el = document.getElementById("route-quote-form");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          🚖 Book Heathrow → Slough Taxi
        </button>

        {/* Mobile bottom sticky CTA */}
        <div className="route-bottom-cta">
          <button
            className="route-bottom-cta-btn"
            onClick={() => {
              const el = document.getElementById("route-quote-form");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            🚖 Book Heathrow → Slough Taxi
          </button>
        </div>

        {/* Spacer so bottom CTA doesn't overlap final content */}
        <div style={{ height: "40px" }}></div>
      </main>
    </div>
  );
};

export default RouteTemplate;
